import * as THREE from 'three';
import { pieceContains } from '../elements/spaces.jsx';

// کف و سقفِ فضاهایی که بیش از یک تکه دارند (L شکل، کنارِ مرز/دیوارِ مورب): همه‌ی تکه‌ها در «یک» هندسه با UVِ
// مشترکِ کلِ فضا — بافت از یک تکه به تکه‌ی بعد پیوسته ادامه پیدا می‌کند (نه از اولِ هر تکه) و فقط یک دست بافت
// بار می‌شود. تکه‌ها مستطیل یا ذوزنقه‌اند (spaces.jsx: xl0/xr0 در y1، xl1/xr1 در y2).

export const corners = (q) =>
  q.xl0 !== undefined
    ? [
        [q.xl0, q.y1],
        [q.xr0, q.y1],
        [q.xr1, q.y2],
        [q.xl1, q.y2],
      ]
    : [
        [q.x1, q.y1],
        [q.x2, q.y1],
        [q.x2, q.y2],
        [q.x1, q.y2],
      ];

export function piecesFrame(pieces) {
  return {
    x1: Math.min(...pieces.map((q) => q.x1)),
    x2: Math.max(...pieces.map((q) => q.x2)),
    y1: Math.min(...pieces.map((q) => q.y1)),
    y2: Math.max(...pieces.map((q) => q.y2)),
  };
}

// بزرگ‌ترین مستطیلِ داخلِ یک تکه (برای شبکه/قابِ تزئینیِ سقف)
export const innerRect = (q) =>
  q.xl0 !== undefined ? { x1: Math.max(q.xl0, q.xl1), x2: Math.min(q.xr0, q.xr1), y1: q.y1, y2: q.y2 } : q;

// هندسه‌ی افقی در ارتفاعِ y (مختصاتِ جهان، سانتی‌متر). up: رو به بالا (کف) یا پایین (سقف).
// uv: نسبت به کادرِ کلِ فضا (frame) — همان قراردادِ planeGeometryِ یک‌تکه، پس repeatِ بافت همان می‌ماند
export function buildSurfaceGeometry(pieces, frame, y, up) {
  const pos = [];
  const uv = [];
  const idx = [];
  const fw = Math.max(1, frame.x2 - frame.x1);
  const fd = Math.max(1, frame.y2 - frame.y1);
  pieces.forEach((q) => {
    const c = corners(q);
    const base = pos.length / 3;
    c.forEach(([x, z]) => {
      pos.push(x, y, z);
      uv.push((x - frame.x1) / fw, up ? (frame.y2 - z) / fd : (z - frame.y1) / fd);
    });
    // (0,1,2) با این ترتیبِ گوشه‌ها رو به پایین است (y بالا، z رو به بیننده)
    if (up) idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
    else idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('uv2', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('uv1', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

const inAny = (pieces, x, z) => pieces.some((p) => pieceContains(p, x, z, -0.01));

// لبه‌های مرزیِ فضا (جایی که دیوار/مرز است، نه درزِ بینِ دو تکه‌ی خودش):
//   افقی/عمودی: { axis: 'x' (z ثابت) | 'z' (x ثابت), c, a, b, n (جهتِ داخل ±۱), convexA, convexB }
//   مورب: { axis: 'd', x0, z0, x1, z1, nx, nz (عمودِ رو به داخل), len }
export function spaceEdges(pieces) {
  const raw = [];
  const diag = [];
  const spanAt = (o, c) => {
    if (Math.abs(c - o.y1) < 0.01) return o.xl0 !== undefined ? [o.xl0, o.xr0] : [o.x1, o.x2];
    if (Math.abs(c - o.y2) < 0.01) return o.xl0 !== undefined ? [o.xl1, o.xr1] : [o.x1, o.x2];
    return [o.x1, o.x2];
  };
  pieces.forEach((p, i) => {
    const others = pieces.filter((_, j) => j !== i);
    const [a0, b0] = spanAt(p, p.y1);
    const [a1, b1] = spanAt(p, p.y2);
    const sides = [
      { axis: 'x', c: p.y1, a: a0, b: b0, n: 1 },
      { axis: 'x', c: p.y2, a: a1, b: b1, n: -1 },
    ];
    const xl0 = p.xl0 !== undefined ? p.xl0 : p.x1;
    const xl1 = p.xl0 !== undefined ? p.xl1 : p.x1;
    const xr0 = p.xl0 !== undefined ? p.xr0 : p.x2;
    const xr1 = p.xl0 !== undefined ? p.xr1 : p.x2;
    [
      [xl0, p.y1, xl1, p.y2, 1],
      [xr0, p.y1, xr1, p.y2, -1],
    ].forEach(([x0, z0, x1, z1, n]) => {
      if (Math.abs(x0 - x1) < 0.01) {
        sides.push({ axis: 'z', c: x0, a: z0, b: z1, n });
        return;
      }
      const len = Math.hypot(x1 - x0, z1 - z0);
      if (len < 1) return;
      // عمود رو به داخل: برای لبه‌ی چپ به سمتِ +x
      let nx = (z1 - z0) / len;
      let nz = -(x1 - x0) / len;
      if (nx * n < 0) {
        nx = -nx;
        nz = -nz;
      }
      const mx = (x0 + x1) / 2 - nx;
      const mz = (z0 + z1) / 2 - nz;
      if (!inAny(others, mx, mz)) diag.push({ axis: 'd', x0, z0, x1, z1, nx, nz, len });
    });
    sides.forEach((e) => {
      if (e.b - e.a < 0.5) return;
      const cutsAt = new Set([e.a, e.b]);
      others.forEach((o) => {
        let lo;
        let hi;
        if (e.axis === 'x') [lo, hi] = spanAt(o, e.c);
        else {
          lo = o.y1;
          hi = o.y2;
        }
        if (lo > e.a && lo < e.b) cutsAt.add(lo);
        if (hi > e.a && hi < e.b) cutsAt.add(hi);
      });
      const ts = [...cutsAt].sort((u, v) => u - v);
      for (let k = 0; k < ts.length - 1; k += 1) {
        const m = (ts[k] + ts[k + 1]) / 2;
        const outC = e.c - e.n * 1;
        const outside = e.axis === 'x' ? !inAny(others, m, outC) : !inAny(others, outC, m);
        if (outside && ts[k + 1] - ts[k] > 0.5) raw.push({ ...e, a: ts[k], b: ts[k + 1] });
      }
    });
  });
  raw.sort((u, v) => (u.axis + u.n + u.c).localeCompare(v.axis + v.n + v.c) || u.a - v.a);
  const merged = [];
  raw.forEach((e) => {
    const last = merged[merged.length - 1];
    if (last && last.axis === e.axis && last.n === e.n && Math.abs(last.c - e.c) < 0.01 && Math.abs(last.b - e.a) < 0.01) last.b = e.b;
    else merged.push({ ...e });
  });
  const E = 2;
  merged.forEach((e) => {
    const inC = e.c + e.n * E;
    const test = (t) => (e.axis === 'x' ? inAny(pieces, t, inC) : inAny(pieces, inC, t));
    e.convexA = !test(e.a - E);
    e.convexB = !test(e.b + E);
  });
  return [...merged, ...diag];
}

// جای یک نوار/جعبه در امتدادِ لبه، از فاصله‌ی o0 تا o1 به سمتِ داخل:
// { position: [x, y, z], size: [طول، ضخامت، پهنا], rotationY } — برای لبه‌ی مورب rotationY دارد
export function edgeBand(e, o0, o1, y, thick) {
  const w = o1 - o0;
  if (e.axis === 'd') {
    const off = (o0 + o1) / 2;
    return {
      position: [(e.x0 + e.x1) / 2 + e.nx * off, y, (e.z0 + e.z1) / 2 + e.nz * off],
      size: [e.len, thick, w],
      rotationY: Math.atan2(-(e.z1 - e.z0), e.x1 - e.x0),
      len: e.len,
    };
  }
  const a = e.a + (e.convexA ? o0 : -o1);
  const b = e.b - (e.convexB ? o0 : -o1);
  const len = Math.max(0, b - a);
  const mid = (a + b) / 2;
  const off = e.c + e.n * ((o0 + o1) / 2);
  return e.axis === 'x'
    ? { position: [mid, y, off], size: [len, thick, w], rotationY: 0, len }
    : { position: [off, y, mid], size: [w, thick, len], rotationY: 0, len };
}

// جهتِ «رو به داخل» برای صفحه‌های گرادیانی (درخششِ سقف، سایه‌ی کنجِ کف)
export const inward = (e) =>
  e.axis === 'd' ? { nx: e.nx, nz: e.nz } : e.axis === 'x' ? { nx: 0, nz: e.n } : { nx: e.n, nz: 0 };

// ---------------------------------------------------------------------------------------------------
// «قاب»های دورتادورِ سقف (نورِ مخفی، پله‌های چندسطحی، حاشیه‌ی دورنگ، قرنیز، درخشش) برای هر شکلی:
// لبه‌ها به حلقه‌های بسته وصل می‌شوند و هر نوار از فاصله‌ی o0 تا o1 به داخل، در گوشه‌ها «فارسی‌بُر» (miter)
// می‌شود — مثلِ قابِ عکس؛ پس در گوشه‌ی مورب نه کوتاه می‌آید و نه از دیوار بیرون می‌زند.

const segOf = (e) => {
  if (e.axis === 'x') return e.n > 0 ? { p: [e.a, e.c], q: [e.b, e.c], n: [0, 1] } : { p: [e.b, e.c], q: [e.a, e.c], n: [0, -1] };
  if (e.axis === 'z') return e.n > 0 ? { p: [e.c, e.b], q: [e.c, e.a], n: [1, 0] } : { p: [e.c, e.a], q: [e.c, e.b], n: [-1, 0] };
  const dx = e.nz;
  const dz = -e.nx;
  const fwd = (e.x1 - e.x0) * dx + (e.z1 - e.z0) * dz > 0;
  return fwd
    ? { p: [e.x0, e.z0], q: [e.x1, e.z1], n: [e.nx, e.nz] }
    : { p: [e.x1, e.z1], q: [e.x0, e.z0], n: [e.nx, e.nz] };
};

// تلورانسِ اتصالِ لبه‌ها: تکه‌های دیوارِ منحنی چند سانت با هم فرق دارند؛ با ۱٫۵ سانت حلقه بسته نمی‌شد
const JOIN_TOL = 12;
const near = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]) < JOIN_TOL;
// نزدیک‌ترین لبه‌ی آزاد (نه اولین لبه‌ای که از راه می‌رسد) — در گوشه‌ی پرازدحام اشتباهی به شاخه‌ی دیگر نمی‌پرد
const nearestFree = (used, segs, pt, key) => {
  let best = -1;
  let bd = Infinity;
  segs.forEach((s, j) => {
    if (used[j]) return;
    const d = Math.hypot(s[key][0] - pt[0], s[key][1] - pt[1]);
    if (d < JOIN_TOL && d < bd) {
      bd = d;
      best = j;
    }
  });
  return best;
};

// ساده‌سازیِ حلقه‌ی بسته (داگلاس-پوکر، ۲٫۵ سانت): پله‌های ریزِ بینِ تکه‌های کف حذف می‌شوند و فقط شکلِ واقعی (گوشه‌ها،
// رأس‌های قوس) می‌ماند — وگرنه عقب‌نشینیِ چندضلعی روی همین زیگزاگ‌ها خودش را قطع می‌کرد
function dp(pts, tol) {
  if (pts.length < 3) return pts;
  const [a] = pts;
  const b = pts[pts.length - 1];
  const dx = b[0] - a[0];
  const dz = b[1] - a[1];
  const L = Math.hypot(dx, dz) || 1;
  let best = -1;
  let bi = 0;
  for (let i = 1; i < pts.length - 1; i += 1) {
    const d = Math.abs((pts[i][0] - a[0]) * dz - (pts[i][1] - a[1]) * dx) / L;
    if (d > best) {
      best = d;
      bi = i;
    }
  }
  if (best <= tol) return [a, b];
  const left = dp(pts.slice(0, bi + 1), tol);
  const right = dp(pts.slice(bi), tol);
  return [...left.slice(0, -1), ...right];
}

function simplifyLoop(loop, tol = 2.5) {
  const pts = loop.segs.map((sg) => sg.p);
  if (pts.length < 4) return loop;
  // دو نقطه‌ی دور از هم = دو لنگر؛ هر نیمه جدا ساده می‌شود
  let far = 0;
  let fd = -1;
  pts.forEach((p, i) => {
    const d = Math.hypot(p[0] - pts[0][0], p[1] - pts[0][1]);
    if (d > fd) {
      fd = d;
      far = i;
    }
  });
  const h1 = dp(pts.slice(0, far + 1), tol);
  const h2 = dp([...pts.slice(far), pts[0]], tol);
  let out = [...h1.slice(0, -1), ...h2.slice(0, -1)];
  if (out.length < 3) return loop;
  // قوس‌ها (پیچ‌های ملایم، کمتر از ~۲۵ درجه) نرم می‌شوند — گوشه‌های واقعیِ اتاق دست‌نخورده
  for (let it = 0; it < 3; it += 1) {
    const next = [];
    const n = out.length;
    out.forEach((p, i) => {
      const a = out[(i - 1 + n) % n];
      const c = out[(i + 1) % n];
      const u = [p[0] - a[0], p[1] - a[1]];
      const v = [c[0] - p[0], c[1] - p[1]];
      const lu = Math.hypot(u[0], u[1]);
      const lv = Math.hypot(v[0], v[1]);
      const cos = lu && lv ? (u[0] * v[0] + u[1] * v[1]) / (lu * lv) : 1;
      if (cos > 0.9 && cos < 0.99995 && lu > 4 && lv > 4) {
        next.push([p[0] - u[0] * 0.25, p[1] - u[1] * 0.25], [p[0] + v[0] * 0.25, p[1] + v[1] * 0.25]);
      } else next.push(p);
    });
    out = next;
  }
  const segs = out.map((p, i) => {
    const q = out[(i + 1) % out.length];
    const L = Math.hypot(q[0] - p[0], q[1] - p[1]) || 1;
    // داخلِ فضا سمتِ چپِ جهتِ لبه است (همان قراردادِ segOf)
    return { p, q, n: [-(q[1] - p[1]) / L, (q[0] - p[0]) / L] };
  });
  return { segs, closed: true };
}

// حلقه‌ها: { segs: [{p, q, n}], closed }
export function edgeLoops(edges) {
  const segs = edges.map(segOf).filter((s) => Math.hypot(s.q[0] - s.p[0], s.q[1] - s.p[1]) > 0.5);
  const used = new Array(segs.length).fill(false);
  const loops = [];
  const opens = [];
  for (let i = 0; i < segs.length; i += 1) {
    if (used[i]) continue;
    used[i] = true;
    const chain = [segs[i]];
    let closed = false;
    // جلو
    for (;;) {
      const last = chain[chain.length - 1];
      if (chain.length > 1 && near(last.q, chain[0].p)) {
        closed = true;
        break;
      }
      const k = nearestFree(used, segs, last.q, 'p');
      if (k < 0) break;
      used[k] = true;
      chain.push(segs[k]);
    }
    if (!closed) {
      // عقب
      for (;;) {
        const first = chain[0];
        const k = nearestFree(used, segs, first.p, 'q');
        if (k < 0) break;
        used[k] = true;
        chain.unshift(segs[k]);
      }
      if (chain.length > 1 && near(chain[chain.length - 1].q, chain[0].p)) closed = true;
    }
    // تکه‌لبه‌های خیلی کوتاه (چند سانت، از سرِ تیزِ مثلث یا درزِ دو تکه) حذف — قاب از کنارشان رد می‌شود؛
    // وگرنه همین تکه‌ی کوچک با کمترین فاصله وارونه می‌شد و قاب را بی‌دلیل باریک می‌کرد
    // (فقط «پله»های کوتاه با پیچِ تند؛ تکه‌های کوتاهِ یک قوسِ نرم یا ادامه‌ی هم‌راستا می‌مانند)
    const turn = (u, v) => u.n[0] * v.n[0] + u.n[1] * v.n[1]; // کسینوسِ زاویه‌ی بینِ دو لبه
    const segsK =
      chain.length > 3
        ? chain.filter((sg, i) => {
            if (Math.hypot(sg.q[0] - sg.p[0], sg.q[1] - sg.p[1]) >= 25) return true;
            const prev = chain[i - 1] || (closed ? chain[chain.length - 1] : null);
            const next = chain[i + 1] || (closed ? chain[0] : null);
            const smooth = (o) => !o || turn(o, sg) > 0.9; // کمتر از ~۲۵ درجه
            return smooth(prev) && smooth(next);
          })
        : chain;
    const kept = segsK.length >= 2 ? segsK : chain;
    // تکه‌لبه‌ی سرگردانِ خیلی کوتاه (درزِ ریزِ بینِ دو تکه‌ی کف) — حلقه نیست، کنار گذاشته می‌شود
    const total = kept.reduce((a, sg) => a + Math.hypot(sg.q[0] - sg.p[0], sg.q[1] - sg.p[1]), 0);
    if (!closed && total < 60) continue;
    if (closed) loops.push(simplifyLoop({ segs: kept, closed }));
    else opens.push(kept);
  }
  // زنجیره‌های بازِ نزدیکِ هم به هم وصل می‌شوند (درزِ چند ده سانتیِ گوشه‌ی قوس و دیوارِ صاف). نتیجه: حلقه‌ی بسته‌ای که
  // دقیقاً شکلِ واقعیِ اتاق (با قوس) است — قاب‌های چندسطحی و نورِ مخفی هم از همین شکل پیروی می‌کنند
  const BRIDGE_TOL = 35;
  let work = opens;
  let progress = true;
  while (progress && work.length) {
    progress = false;
    for (let i = 0; i < work.length && !progress; i += 1) {
      const A = work[i];
      const endA = A[A.length - 1].q;
      const startA = A[0].p;
      if (Math.hypot(endA[0] - startA[0], endA[1] - startA[1]) < BRIDGE_TOL) {
        loops.push(simplifyLoop({ segs: A, closed: true }));
        work = work.filter((_, k) => k !== i);
        progress = true;
        break;
      }
      let bj = -1;
      let bd = Infinity;
      for (let j = 0; j < work.length; j += 1) {
        if (j === i) continue;
        const d = Math.hypot(work[j][0].p[0] - endA[0], work[j][0].p[1] - endA[1]);
        if (d < BRIDGE_TOL && d < bd) {
          bd = d;
          bj = j;
        }
      }
      if (bj < 0) continue;
      const merged = [...A, ...work[bj]];
      work = work.filter((_, k) => k !== i && k !== bj);
      work.push(merged);
      progress = true;
    }
  }
  work.forEach((chain) => {
    const total = chain.reduce((a, sg) => a + Math.hypot(sg.q[0] - sg.p[0], sg.q[1] - sg.p[1]), 0);
    if (total >= 60) loops.push({ segs: chain, closed: false });
  });
  return loops;
}

// بردارِ «فارسی‌بُر» در رأسِ بینِ دو لبه (برای فاصله‌ی ۱)
function miter(nA, nB) {
  const d = 1 + nA[0] * nB[0] + nA[1] * nB[1];
  if (d < 0.25) return [(nA[0] + nB[0]) * 2, (nA[1] + nB[1]) * 2]; // گوشه‌ی خیلی تیز — محدود
  return [(nA[0] + nB[0]) / d, (nA[1] + nB[1]) / d];
}

// چهارضلعیِ هر لبه بینِ o0 و o1: [A0, B0, B1, A1] (A = سرِ لبه، B = تهِ لبه؛ ۰ = سمتِ دیوار، ۱ = داخل)
// keep: لبه‌های حذف‌شده — همسایه‌ی حذف‌شده یعنی سرِ نوار صاف (عمود) تمام شود، نه فارسی‌بُر
export function loopQuads(loop, o0, o1, keep = null) {
  const { segs, closed } = loop;
  const n = segs.length;
  const ok = (sg) => sg && (!keep || keep(sg));
  return segs.map((s, i) => {
    const prev0 = i > 0 ? segs[i - 1] : closed ? segs[n - 1] : null;
    const next0 = i < n - 1 ? segs[i + 1] : closed ? segs[0] : null;
    const prev = ok(prev0) ? prev0 : null;
    const next = ok(next0) ? next0 : null;
    const ms = prev ? miter(prev.n, s.n) : s.n;
    const me = next ? miter(s.n, next.n) : s.n;
    const at = (pt, m, d) => [pt[0] + m[0] * d, pt[1] + m[1] * d];
    return { quad: [at(s.p, ms, o0), at(s.q, me, o0), at(s.q, me, o1), at(s.p, ms, o1)], n: s.n, seg: s };
  });
}

// نوارِ یک لبه‌ی کوتاه که در این فاصله وارونه شده — کشیده نمی‌شود
const flipped = ([A0, B0, B1, A1]) => (B1[0] - A1[0]) * (B0[0] - A0[0]) + (B1[1] - A1[1]) * (B0[1] - A0[1]) <= 0;

function finish(pos, uv, idx) {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('uv1', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('uv2', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

// نوارِ تخت (رو به پایین) در ارتفاعِ y. uv: v=۰ سمتِ دیوار (o0)، v=۱ داخل (o1) — برای گرادیانِ درخشش/سایه.
// frame داده شود → uv نسبت به کادرِ فضا (برای بافت)
// keep(seg): فقط لبه‌هایی که true می‌دهد (مثلاً فقط کنارِ دیوارِ واقعی، نه «مرزِ فضا»)
export function ringFlatGeometry(loops, o0, o1, y, frame = null, keep = null) {
  const pos = [];
  const uv = [];
  const idx = [];
  loops.forEach((loop) => {
    loopQuads(loop, o0, o1, keep).forEach(({ quad, seg }) => {
      if (keep && !keep(seg)) return;
      if (flipped(quad)) return;
      const b = pos.length / 3;
      quad.forEach(([x, z], k) => {
        pos.push(x, y, z);
        if (frame) uv.push((x - frame.x1) / Math.max(1, frame.x2 - frame.x1), (z - frame.y1) / Math.max(1, frame.y2 - frame.y1));
        else uv.push(k === 0 || k === 3 ? 0 : 1, k < 2 ? 0 : 1);
      });
      idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
    });
  });
  return finish(pos, uv, idx);
}

// نوارِ حجیم (پله/حاشیه/قرنیز/نوارِ LED): کفِ پایینی + دیواره‌ی داخلی (+ دیواره‌ی بیرونی اگر از دیوار فاصله دارد)
export function ringPrismGeometry(loops, o0, o1, yBottom, yTop, frame = null, keep = null) {
  const pos = [];
  const uv = [];
  const idx = [];
  const fw = frame ? Math.max(1, frame.x2 - frame.x1) : 100;
  const fd = frame ? Math.max(1, frame.y2 - frame.y1) : 100;
  const fx = frame ? frame.x1 : 0;
  const fz = frame ? frame.y1 : 0;
  const quad = (pts) => {
    const b = pos.length / 3;
    pts.forEach(([x, y, z, u, v]) => {
      pos.push(x, y, z);
      uv.push(u, v);
    });
    idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
  };
  loops.forEach((loop) => {
    loopQuads(loop, o0, o1, keep).forEach(({ quad: [A0, B0, B1, A1], seg }) => {
      if (keep && !keep(seg)) return;
      if (flipped([A0, B0, B1, A1])) return;
      const flat = ([x, z]) => [x, yBottom, z, (x - fx) / fw, (z - fz) / fd];
      quad([flat(A0), flat(B0), flat(B1), flat(A1)]);
      const len = Math.hypot(B1[0] - A1[0], B1[1] - A1[1]) / fw;
      const h = (yTop - yBottom) / fd;
      quad([
        [A1[0], yBottom, A1[1], 0, 0],
        [B1[0], yBottom, B1[1], len, 0],
        [B1[0], yTop, B1[1], len, h],
        [A1[0], yTop, A1[1], 0, h],
      ]);
      if (o0 > 0.5) {
        quad([
          [A0[0], yBottom, A0[1], 0, 0],
          [B0[0], yBottom, B0[1], len, 0],
          [B0[0], yTop, B0[1], len, h],
          [A0[0], yTop, A0[1], 0, h],
        ]);
      }
    });
  });
  return finish(pos, uv, idx);
}

// بیشترین فاصله‌ای که قاب می‌تواند به داخل برود بی‌آن‌که برعکس شود یا از فضا بیرون بزند (فضای کوچک/مثلثی):
// همه‌ی رأس‌های قاب داخلِ فضا باشند و هیچ لبه‌ای وارونه نشود
export function maxRingInset(loops, pieces, limit = 400) {
  const ok = (d) =>
    loops.every((loop) =>
      loopQuads(loop, 0, d).every(({ quad: [A0, B0, B1, A1], seg }) => {
        // تکه‌لبه‌ی خیلی کوتاه (چند سانت، کنارِ گوشه) پهنای کلِ قاب را محدود نکند — موقعِ کشیدن کنار می‌رود
        if (Math.hypot(seg.q[0] - seg.p[0], seg.q[1] - seg.p[1]) < 12) return true;
        if ((B1[0] - A1[0]) * (B0[0] - A0[0]) + (B1[1] - A1[1]) * (B0[1] - A0[1]) <= 0) return false;
        return pieces.some((p) => pieceContains(p, A1[0], A1[1], 0.5)) && pieces.some((p) => pieceContains(p, B1[0], B1[1], 0.5));
      })
    );
  let lo = 0;
  let hi = limit;
  if (ok(hi)) return hi;
  for (let i = 0; i < 14; i += 1) {
    const mid = (lo + hi) / 2;
    if (ok(mid)) lo = mid;
    else hi = mid;
  }
  return lo;
}

// آیا این لبه کنارِ یک دیوارِ واقعی است؟ (walls = فقط دیوارهای واقعی؛ «مرزِ فضا» در سه‌بعدی دیوار ندارد)
export function segOnWall(seg, walls, tol = 7) {
  const mx = (seg.p[0] + seg.q[0]) / 2;
  const mz = (seg.p[1] + seg.q[1]) / 2;
  return (walls || []).some((w) => {
    const dx = w.x2 - w.x1;
    const dy = w.y2 - w.y1;
    const l2 = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((mx - w.x1) * dx + (mz - w.y1) * dy) / l2));
    return Math.hypot(mx - (w.x1 + dx * t), mz - (w.y1 + dy * t)) <= tol;
  });
}

// ---------------------------------------------------------------------------------------------------
// «عقب‌نشینیِ» درستِ چندضلعی (offset) با حذفِ لبه‌هایی که در این فاصله از بین می‌روند — قابِ پهن (پله‌ی چندسطحی، هاله‌ی
// نور) در گوشه‌های تیز و کنارِ قوس هم درست و یک‌تکه می‌ماند. هر قاب = ناحیه‌ی بینِ دو چندضلعیِ عقب‌نشسته (o0 و o1).

function lineMeet(a, b, d) {
  // تقاطعِ دو خطِ عقب‌نشسته (a و b، هر کدام p + n·d در امتدادِ لبه)
  const ax = a.p[0] + a.n[0] * d;
  const az = a.p[1] + a.n[1] * d;
  const bx = b.p[0] + b.n[0] * d;
  const bz = b.p[1] + b.n[1] * d;
  const ux = a.q[0] - a.p[0];
  const uz = a.q[1] - a.p[1];
  const vx = b.q[0] - b.p[0];
  const vz = b.q[1] - b.p[1];
  const den = ux * vz - uz * vx;
  if (Math.abs(den) < 1e-9 * Math.hypot(ux, uz) * Math.hypot(vx, vz)) return [bx, bz]; // هم‌راستا
  const t = ((bx - ax) * vz - (bz - az) * vx) / den;
  return [ax + ux * t, az + uz * t];
}

const polyArea = (pts) => {
  let a = 0;
  for (let i = 0; i < pts.length; i += 1) {
    const [x1, z1] = pts[i];
    const [x2, z2] = pts[(i + 1) % pts.length];
    a += x1 * z2 - x2 * z1;
  }
  return a / 2;
};

// چندضلعیِ عقب‌نشسته‌ی یک حلقه‌ی بسته در فاصله‌ی d (null اگر چیزی نماند)
export function offsetLoop(loop, d) {
  const r = offsetLoopKeep(loop, d);
  return r ? r.pts : null;
}

// همان، به‌اضافه‌ی این‌که هر رأس سرِ کدام لبه‌ی اصلی است (keep[k] = شماره‌ی لبه) — برای وصل کردنِ دو قاب لبه‌به‌لبه
export function offsetLoopKeep(loop, d) {
  let S = loop.segs.map((sg, i) => ({ ...sg, oi: i }));
  if (!loop.closed || S.length < 3) return null;
  const sign0 = Math.sign(polyArea(S.map((s) => s.p)));
  if (d <= 0.01) return { pts: S.map((s) => s.p), keep: S.map((s) => s.oi) };
  for (let iter = 0; iter < loop.segs.length; iter += 1) {
    if (S.length < 3) return null;
    const V = S.map((s, i) => lineMeet(S[(i - 1 + S.length) % S.length], s, d));
    // لبه‌ای که وارونه شده (سرِ دومش پیش از سرِ اولش افتاده) حذف می‌شود
    let worst = -1;
    let worstLen = Infinity;
    S.forEach((s, i) => {
      const a = V[i];
      const b = V[(i + 1) % S.length];
      const ux = s.q[0] - s.p[0];
      const uz = s.q[1] - s.p[1];
      const dot = (b[0] - a[0]) * ux + (b[1] - a[1]) * uz;
      if (dot < 0) {
        const L = Math.hypot(ux, uz);
        if (L < worstLen) {
          worstLen = L;
          worst = i;
        }
      }
    });
    if (worst < 0) {
      const a = polyArea(V);
      if (Math.sign(a) !== sign0 || Math.abs(a) < 1) return null;
      return { pts: V, keep: S.map((s) => s.oi) };
    }
    S = S.filter((_, i) => i !== worst);
  }
  return null;
}

const insideAnyPiece = (pieces, x, z) => pieces.some((p) => pieceContains(p, x, z, 1));

// ناحیه‌ی بینِ دو چندضلعی → مثلث‌ها (THREE.ShapeUtils) در ارتفاعِ y؛ uv: «glow» = v از ۰ (o0) تا ۱ (o1)، «frame» = کادرِ فضا
function ringBetween(A, B, y, uvMode, frame, o0, o1, segs, out) {
  const fuv = (x, z) => [(x - frame.x1) / Math.max(1, frame.x2 - frame.x1), (z - frame.y1) / Math.max(1, frame.y2 - frame.y1)];
  // رأس‌های دو چندضلعی یک‌به‌یک (هیچ لبه‌ای نیفتاده — حالتِ معمول): یک چهارضلعی برای هر لبه، گرادیان دقیقاً عمود بر لبه
  if (B && A.length === B.length) {
    for (let i = 0; i < A.length; i += 1) {
      const j = (i + 1) % A.length;
      const q = [A[i], A[j], B[j], B[i]];
      const base = out.pos.length / 3;
      q.forEach(([x, z], k) => {
        out.pos.push(x, y, z);
        if (uvMode === 'glow') out.uv.push(k === 0 || k === 3 ? 0 : 1, k < 2 ? 0 : 1);
        else out.uv.push(...fuv(x, z));
      });
      out.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
    return;
  }
  // بزرگ‌تر = بیرونی
  const outer = Math.abs(polyArea(A)) >= Math.abs(polyArea(B)) ? A : B;
  const hole = outer === A ? B : A;
  const contour = outer.map(([x, z]) => new THREE.Vector2(x, z));
  const holes = hole && hole.length >= 3 ? [hole.map(([x, z]) => new THREE.Vector2(x, z))] : [];
  const tris = THREE.ShapeUtils.triangulateShape(contour, holes);
  const all = [...contour, ...(holes[0] || [])];
  const base = out.pos.length / 3;
  const dist = (x, z) => {
    let best = Infinity;
    segs.forEach((s) => {
      const ux = s.q[0] - s.p[0];
      const uz = s.q[1] - s.p[1];
      const l2 = ux * ux + uz * uz || 1;
      const t = Math.max(0, Math.min(1, ((x - s.p[0]) * ux + (z - s.p[1]) * uz) / l2));
      best = Math.min(best, Math.hypot(x - (s.p[0] + ux * t), z - (s.p[1] + uz * t)));
    });
    return best;
  };
  all.forEach((v) => {
    out.pos.push(v.x, y, v.y);
    if (uvMode === 'glow') out.uv.push(0.5, Math.max(0, Math.min(1, (dist(v.x, v.y) - o0) / Math.max(0.01, o1 - o0))));
    else out.uv.push((v.x - frame.x1) / Math.max(1, frame.x2 - frame.x1), (v.y - frame.y1) / Math.max(1, frame.y2 - frame.y1));
  });
  tris.forEach(([a, b, c]) => out.idx.push(base + a, base + b, base + c));
}

// قابِ تخت (رو به پایین) بینِ o0 و o1 برای همه‌ی حلقه‌ها. حلقه‌ی باز → همان روشِ لبه‌به‌لبه
// رأسِ «سرِ لبه‌ی j» در یک چندضلعیِ عقب‌نشسته — اگر آن لبه افتاده، نقطه‌ای که لبه در آن جمع شد (سرِ لبه‌ی بعدیِ مانده)
function vertexOf(res, j, n) {
  for (let s = 0; s < n; s += 1) {
    const k = res.keep.indexOf((j + s) % n);
    if (k >= 0) return res.pts[k];
  }
  return res.pts[0];
}

// قاب بینِ دو عقب‌نشینی، لبه‌به‌لبه: هر لبه‌ی اصلی یک چهارضلعی (اگر در یکی جمع شده بود، مثلث)
function ringQuads(RA, RB, n, y, uvMode, frame, out) {
  const fuv = (x, z) => [(x - frame.x1) / Math.max(1, frame.x2 - frame.x1), (z - frame.y1) / Math.max(1, frame.y2 - frame.y1)];
  for (let j = 0; j < n; j += 1) {
    const q = [vertexOf(RA, j, n), vertexOf(RA, (j + 1) % n, n), vertexOf(RB, (j + 1) % n, n), vertexOf(RB, j, n)];
    if (q[0] === q[1] && q[2] === q[3]) continue; // در هر دو جمع شده
    const base = out.pos.length / 3;
    q.forEach(([x, z], k) => {
      out.pos.push(x, y, z);
      if (uvMode === 'glow') out.uv.push(k === 0 || k === 3 ? 0 : 1, k < 2 ? 0 : 1);
      else out.uv.push(...fuv(x, z));
    });
    out.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
}

export function ringShapeGeometry(loops, o0, o1, y, uvMode = 'glow', frame = null) {
  const out = { pos: [], uv: [], idx: [] };
  loops.forEach((loop) => {
    if (!loop.closed) return;
    const RA = offsetLoopKeep(loop, o0);
    if (!RA) return;
    const RB = offsetLoopKeep(loop, o1);
    if (RB) ringQuads(RA, RB, loop.segs.length, y, uvMode, frame, out);
    else ringBetween(RA.pts, null, y, uvMode, frame, o0, o1, loop.segs, out);
  });
  if (!out.idx.length) return null;
  return finish(out.pos, out.uv, out.idx);
}

// قابِ حجیم: کفِ پایینی (ringShape) + دیواره‌ی داخلی (روی چندضلعیِ o1) + بیرونی (روی o0، اگر از دیوار فاصله دارد)
export function ringSolidGeometry(loops, o0, o1, yBottom, yTop, frame) {
  const out = { pos: [], uv: [], idx: [] };
  const fw = Math.max(1, frame.x2 - frame.x1);
  const wall = (P, h) => {
    for (let i = 0; i < P.length; i += 1) {
      const a = P[i];
      const b = P[(i + 1) % P.length];
      const base = out.pos.length / 3;
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]) / fw;
      out.pos.push(a[0], yBottom, a[1], b[0], yBottom, b[1], b[0], yTop, b[1], a[0], yTop, a[1]);
      out.uv.push(0, 0, L, 0, L, h, 0, h);
      out.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
  };
  loops.forEach((loop) => {
    if (!loop.closed) return;
    const RA = offsetLoopKeep(loop, o0);
    if (!RA) return;
    const RB = offsetLoopKeep(loop, o1);
    if (RB) ringQuads(RA, RB, loop.segs.length, yBottom, 'frame', frame, out);
    else ringBetween(RA.pts, null, yBottom, 'frame', frame, o0, o1, loop.segs, out);
    if (RB) wall(RB.pts, (yTop - yBottom) / fw);
    if (o0 > 0.5) wall(RA.pts, (yTop - yBottom) / fw);
  });
  if (!out.idx.length) return null;
  return finish(out.pos, out.uv, out.idx);
}

// بیشترین فاصله‌ی امن (نسخه‌ی درست): چندضلعیِ عقب‌نشسته هنوز وجود دارد و همه‌ی رأس‌هایش داخلِ فضاست
// نسخه‌ی مقاوم در برابر دیوار منحنی: جستجوی دودویی روی عقب‌نشینیِ چندضلعی فرض می‌کرد «اگر d امن است، هر d کوچک‌تر
// هم امن است». روی دیوارِ منحنی این فرض غلط است و نتیجه نزدیک صفر می‌شد (و نور مخفی/چندسطحی حذف). این‌جا
// نقاطِ داخلِ فضا را روی شبکه نمونه‌برداری می‌کنیم و بیشترین فاصله‌ی هر نقطه تا نزدیک‌ترین لبه را می‌گیریم.
function distToSeg(px, pz, ax, az, bx, bz) {
  const dx = bx - ax;
  const dz = bz - az;
  const len2 = dx * dx + dz * dz;
  const t = len2 > 1e-9 ? Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / len2)) : 0;
  return Math.hypot(px - (ax + dx * t), pz - (az + dz * t));
}

export function maxInset(loops, pieces, limit = 400) {
  if (!pieces || !pieces.length) return 0;
  // لبه‌های مرزیِ واقعی فضا (از همان spaceEdges که قاب‌ها هم از آن ساخته می‌شوند) — حتی اگر حلقه‌ها بسته نشده باشند
  const segsAll = spaceEdges(pieces).map((e) => {
    const s = segOf(e);
    return [s.p, s.q];
  });
  if (!segsAll.length) return 0;
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  pieces.forEach((p) => {
    minX = Math.min(minX, p.x1, p.x2);
    maxX = Math.max(maxX, p.x1, p.x2);
    minZ = Math.min(minZ, p.y1, p.y2);
    maxZ = Math.max(maxZ, p.y1, p.y2);
  });
  const step = 8;
  let best = 0;
  for (let x = minX + step / 2; x <= maxX; x += step) {
    for (let z = minZ + step / 2; z <= maxZ; z += step) {
      if (!insideAnyPiece(pieces, x, z)) continue;
      let d = Infinity;
      for (const [a, b] of segsAll) d = Math.min(d, distToSeg(x, z, a[0], a[1], b[0], b[1]));
      if (d > best) best = d;
    }
  }
  return Math.min(limit, best);
}
